import { Injectable } from '@angular/core';
import {
  localDatePlusYearsInput,
  localTodayDateInput,
  toLocalDateInput,
} from '../../utils/date-input.util';
import {
  AdvanceQuotaOption,
  AdvanceQuotaOptionTableDto,
  AdvanceVehicleTypeOption,
  QuotaCardAdvanceActivateCode,
  QuotaCardAdvanceDetail,
  QuotaCardAdvanceListRow,
  QuotaCardAdvanceTableDto,
  SaveQuotaCardAdvancePayload,
  QuotaCardAdvanceActionPayload,
} from '../../models/card-quota/quota-card-advance.model';

@Injectable({ providedIn: 'root' })
export class MapperQuotaCardAdvanceService {
  mapTable(dto: QuotaCardAdvanceTableDto): {
    items: QuotaCardAdvanceListRow[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } {
    const raw = dto as QuotaCardAdvanceTableDto & Record<string, unknown>;
    const rows = (dto.data ?? raw['Data'] ?? []) as QuotaCardAdvanceListRow[];
    return {
      items: rows.map((r) => this.mapListRow(r as QuotaCardAdvanceListRow & Record<string, unknown>)),
      total: Number(dto.total ?? raw['Total'] ?? 0),
      page: Number(dto.page ?? raw['Page'] ?? 1),
      pageSize: Number(dto.pageSize ?? raw['PageSize'] ?? 20),
      totalPages: Number(dto.totalPages ?? raw['TotalPages'] ?? 0),
    };
  }

  mapListRow(row: QuotaCardAdvanceListRow & Record<string, unknown>): QuotaCardAdvanceListRow {
    return {
      id: Number(row.id ?? row['Id'] ?? 0),
      name: String(row.name ?? row['Name'] ?? ''),
      clientId: Number(row.clientId ?? row['ClientId'] ?? 0),
      clientName: String(row.clientName ?? row['ClientName'] ?? ''),
      siteId: Number(row.siteId ?? row['SiteId'] ?? 0),
      cardTypeId: Number(row.cardTypeId ?? row['CardTypeId'] ?? 0),
      cardTypeName: String(row.cardTypeName ?? row['CardTypeName'] ?? ''),
      vehicleTypeId: Number(row.vehicleTypeId ?? row['VehicleTypeId'] ?? 0),
      vehicleTypeName: String(row.vehicleTypeName ?? row['VehicleTypeName'] ?? ''),
      qty: Number(row.qty ?? row['Qty'] ?? 0),
      status: Number(row.status ?? row['Status'] ?? 0),
      quotaId: row.quotaId != null ? Number(row.quotaId ?? row['QuotaId']) : null,
      quotaName: String(row.quotaName ?? row['QuotaName'] ?? ''),
      quotaRemaining: Number(row.quotaRemaining ?? row['QuotaRemaining'] ?? 0),
      startDate: row.startDate != null ? toLocalDateInput(String(row.startDate ?? row['StartDate'])) : null,
      endDate: row.endDate != null ? toLocalDateInput(String(row.endDate ?? row['EndDate'])) : null,
      createDate: row.createDate != null ? String(row.createDate ?? row['CreateDate']) : null,
      activateCodeCount: Number(row.activateCodeCount ?? row['ActivateCodeCount'] ?? 0),
      advanceKind: String(row.advanceKind ?? row['AdvanceKind'] ?? 'client_card_quota'),
      activateExpireDays: Number(
        row.activateExpireDays ?? row['ActivateExpireDays'] ?? 10
      ),
    };
  }

  mapDetail(dto: QuotaCardAdvanceDetail & Record<string, unknown>): QuotaCardAdvanceDetail {
    const base = this.mapListRow(dto);
    return {
      ...base,
      subscriptionId: Number(dto.subscriptionId ?? dto['SubscriptionId'] ?? 0),
      subscriptionName: String(dto.subscriptionName ?? dto['SubscriptionName'] ?? ''),
      remark: dto.remark != null ? String(dto.remark ?? dto['Remark']) : null,
    };
  }

  mapVehicleTypes(rows: AdvanceVehicleTypeOption[] | Record<string, unknown>[]): AdvanceVehicleTypeOption[] {
    return (rows ?? []).map((row) => {
      const r = row as AdvanceVehicleTypeOption & Record<string, unknown>;
      return {
        id: Number(r.id ?? r['Id'] ?? 0),
        name: String(r.name ?? r['Name'] ?? ''),
        quotaCount: Number(r.quotaCount ?? r['QuotaCount'] ?? 0),
      };
    });
  }

  mapAdvanceQuotasList(rows: AdvanceQuotaOption[] | Record<string, unknown>[]): AdvanceQuotaOption[] {
    return (rows ?? []).map((row) => this.mapAdvanceQuota(row as AdvanceQuotaOption & Record<string, unknown>));
  }

  quotaLevelLabel(level: string): string {
    if (level === 'master') return 'Master';
    if (level === 'sub') return 'Sub';
    return level || '-';
  }

  mapAdvanceQuotas(dto: AdvanceQuotaOptionTableDto): { items: AdvanceQuotaOption[]; total: number } {
    const raw = dto as AdvanceQuotaOptionTableDto & Record<string, unknown>;
    const rows = (dto.data ?? raw['Data'] ?? []) as AdvanceQuotaOption[];
    return {
      items: rows.map((row) => this.mapAdvanceQuota(row as AdvanceQuotaOption & Record<string, unknown>)),
      total: Number(dto.total ?? raw['Total'] ?? rows.length),
    };
  }

  mapAdvanceQuota(row: AdvanceQuotaOption & Record<string, unknown>): AdvanceQuotaOption {
    return {
      id: Number(row.id ?? row['Id'] ?? 0),
      name: String(row.name ?? row['Name'] ?? ''),
      cardTypeId: Number(row.cardTypeId ?? row['CardTypeId'] ?? 0),
      cardTypeName: String(row.cardTypeName ?? row['CardTypeName'] ?? ''),
      subscriptionId: Number(row.subscriptionId ?? row['SubscriptionId'] ?? 0),
      subscriptionName: String(row.subscriptionName ?? row['SubscriptionName'] ?? ''),
      vehicleTypeId: Number(row.vehicleTypeId ?? row['VehicleTypeId'] ?? 0),
      vehicleTypeName: String(row.vehicleTypeName ?? row['VehicleTypeName'] ?? ''),
      quotaLevel: String(row.quotaLevel ?? row['QuotaLevel'] ?? ''),
      quotaApproved: Number(row.quotaApproved ?? row['QuotaApproved'] ?? 0),
      quotaUsed: Number(row.quotaUsed ?? row['QuotaUsed'] ?? 0),
      quotaRemaining: Number(row.quotaRemaining ?? row['QuotaRemaining'] ?? 0),
    };
  }

  mapActivateCodes(rows: QuotaCardAdvanceActivateCode[] | Record<string, unknown>[]): QuotaCardAdvanceActivateCode[] {
    return (rows ?? []).map((row) => {
      const r = row as QuotaCardAdvanceActivateCode & Record<string, unknown>;
      return {
        id: Number(r.id ?? r['Id'] ?? 0),
        code: String(r.code ?? r['Code'] ?? ''),
        isUsed: Boolean(r.isUsed ?? r['IsUsed'] ?? false),
        startDate: r.startDate != null ? String(r.startDate ?? r['StartDate']) : null,
        endDate: r.endDate != null ? String(r.endDate ?? r['EndDate']) : null,
        activateDate: r.activateDate != null ? String(r.activateDate ?? r['ActivateDate']) : null,
        activateExpireAt:
          r.activateExpireAt != null ? String(r.activateExpireAt ?? r['ActivateExpireAt']) : null,
      };
    });
  }

  statusLabel(status: number): string {
    const map: Record<number, string> = {
      1: 'Draft',
      2: 'อนุมัติแล้ว',
      3: 'สร้างรหัสแล้ว',
      4: 'รออนุมัติ',
      5: 'ปฏิเสธ',
      6: 'ยกเลิก',
    };
    return map[status] ?? String(status);
  }

  createEmptyForm(siteId: number): QuotaCardAdvanceDetail {
    return {
      id: 0,
      name: '',
      clientId: 0,
      clientName: '',
      siteId,
      cardTypeId: 0,
      cardTypeName: '',
      vehicleTypeId: 0,
      vehicleTypeName: '',
      qty: 1,
      status: 1,
      quotaId: null,
      quotaName: '',
      quotaRemaining: 0,
      startDate: localTodayDateInput(),
      endDate: localDatePlusYearsInput(1),
      createDate: null,
      subscriptionId: 0,
      subscriptionName: '',
      remark: null,
      activateCodeCount: 0,
      advanceKind: 'client_card_quota',
      activateExpireDays: 10,
    };
  }

  buildSavePayload(
    form: QuotaCardAdvanceDetail,
    userId: number,
    menuId: number,
    submitForApproval: boolean
  ): SaveQuotaCardAdvancePayload {
    return {
      userId,
      menuId,
      id: form.id > 0 ? form.id : null,
      clientId: form.clientId,
      siteId: form.siteId,
      quotaId: form.quotaId ?? 0,
      vehicleTypeId: form.vehicleTypeId,
      qty: form.qty,
      startDate: form.startDate ?? '',
      endDate: form.endDate ?? '',
      remark: form.remark,
      submitForApproval,
      activateExpireDays: form.activateExpireDays > 0 ? form.activateExpireDays : 10,
    };
  }

  buildActionPayload(id: number, userId: number, menuId: number): QuotaCardAdvanceActionPayload {
    return { id, userId, menuId };
  }
}
